import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, KpiCard, StatusBadge, EmptyState } from '../../components/common/PageHeader';
import BerufskodexModal from '../../components/common/BerufskodexModal';
import api from '../../lib/api';
import { Link } from 'react-router-dom';
import { Briefcase, Clock, CheckCircle, ExternalLink, UserCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { statusLabel, serviceTypeLabel, meetingToolLabel } from '../../lib/constants';

const InterpreterDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showKodex, setShowKodex] = useState(false);

  const reloadProfile = async () => {
    try {
      const p = await api.get('/interpreters/me');
      setProfile(p.data);
      setShowKodex(!p.data?.code_of_conduct_accepted);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Profile reload failed:', err);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, j, p] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/jobs'),
          api.get('/interpreters/me'),
        ]);
        setStats(s.data); setJobs(j.data); setProfile(p.data);
        setShowKodex(!p.data?.code_of_conduct_accepted);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Interpreter dashboard load failed:', err);
        }
      }
    })();
  }, []);

  if (!stats) return <AppLayout><div>Lade...</div></AppLayout>;

  const pending = user?.status === 'pending' || !profile?.active_status;
  const requested = jobs.filter(j => j.status === 'dolmetscher_angefragt');
  const confirmed = jobs.filter(j => ['dolmetscher_bestaetigt', 'bestaetigt', 'meetinglink_versendet'].includes(j.status));

  return (
    <AppLayout>
      <BerufskodexModal open={showKodex} onAccepted={() => { setShowKodex(false); reloadProfile(); }} />
      <PageHeader title={`Hallo, ${profile?.first_name || user?.name}`} description="Ihre Aufträge und Verfügbarkeit" />

      {pending && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex gap-3" data-testid="pending-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <div>
            <div className="font-medium text-amber-900">Ihr Konto wartet auf Freischaltung</div>
            <div className="text-sm text-amber-800 mt-0.5">Unser Team prüft Ihre Angaben. Sie werden benachrichtigt, sobald Sie freigeschaltet sind und Aufträge annehmen können.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <KpiCard label="Neue Anfragen" value={stats.requested} icon={AlertTriangle} accent="text-amber-600" testid="kpi-requested" />
        <KpiCard label="Bestätigte Termine" value={stats.confirmed} icon={CheckCircle} accent="text-emerald-600" testid="kpi-confirmed" />
        <KpiCard label="Abgeschlossen" value={stats.completed} icon={Briefcase} testid="kpi-completed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {requested.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md card-subtle">
              <div className="px-5 py-4 border-b border-slate-200">
                <h2 className="font-heading text-base font-medium text-slate-900">Offene Anfragen</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {requested.map(j => (
                  <Link key={j.id} to={`/interpreter/jobs`} className="block p-4 hover:bg-slate-50" data-testid={`requested-${j.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{j.source_language} → {j.target_language} · {serviceTypeLabel(j.service_type)}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{j.date} {j.time} · {j.duration} Min · {j.specialization || 'Allgemein'}</div>
                      </div>
                      <StatusBadge status={j.status} label={statusLabel(j.status)} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md card-subtle">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading text-base font-medium text-slate-900">Kommende bestätigte Termine</h2>
            </div>
            {confirmed.length === 0 ? (
              <div className="p-6"><EmptyState icon={Briefcase} title="Keine bestätigten Termine" description="Bestätigen Sie offene Anfragen, um hier Termine zu sehen." /></div>
            ) : (
              <div className="divide-y divide-slate-100">
                {confirmed.map(j => (
                  <div key={j.id} className="p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{j.date} {j.time} · {j.source_language} → {j.target_language}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{serviceTypeLabel(j.service_type)} · {j.duration} Min</div>
                    </div>
                    {j.meeting_link ? (
                      <a href={j.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded px-2 py-1" data-testid={`meeting-${j.id}`}>
                        <ExternalLink className="w-3 h-3" /> {meetingToolLabel(j.meeting_tool) || 'Meeting'}
                      </a>
                    ) : <StatusBadge status={j.status} label={statusLabel(j.status)} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Mein Profil</h3>
            {profile && (
              <div className="space-y-2 text-sm text-slate-700">
                <div><span className="text-slate-500">Sprachen:</span> {profile.languages.join(', ') || '-'}</div>
                <div><span className="text-slate-500">Ziel:</span> {profile.target_languages.join(', ') || '-'}</div>
                <div><span className="text-slate-500">Fachgebiete:</span> {profile.specializations.join(', ') || '-'}</div>
                <div><span className="text-slate-500">Honorar:</span> <span className="font-mono">{profile.custom_rate.toFixed(2)} €/Std</span></div>
                <div><span className="text-slate-500">Verfügbarkeit:</span> {profile.availability_status}</div>
              </div>
            )}
            <Link to="/interpreter/profile" className="mt-4 inline-flex items-center gap-2 text-xs text-[#0F4C81] hover:underline" data-testid="link-profile">
              <UserCircle2 className="w-3 h-3" /> Profil bearbeiten
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default InterpreterDashboard;
